import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Button } from '@abyss/web/ui/Button';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Popover } from '@abyss/web/ui/Popover';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { find } from 'lodash';
import isArray from 'lodash/isArray';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import {
  useEducationalContentStatus,
  useSaveEducationalContentStatus,
} from '../../hooks/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import {
  Option,
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../models/ProviderSearch';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { adobeSearchFilterEvent } from '../AdobeTagging/adobeSearchFilterEvent';
import { CheckBoxGroup } from '../CheckBoxGroup';
import {
  Constants,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  FilterOptionsType,
  ReverseCoverageTypesCodes,
  SectionType,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { viewFilterSortScreen } from '../ConstantsStyles';
import { RadioGroup } from '../RadioGroup';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import {
  getFormattedFilters,
  getSearchTerm,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { renderFilterLabel } from '../Utils/filters.utils';
import { GroupedAoeFilter } from './AllFilterDrawer/ProviderFilters/GroupedAoeFilter';
import {
  isFilterDisabledByBcc,
  isPrioritySchedulingActive,
} from './BccFilterUtils';
import {
  FilterPopOverWrapper,
  FilterToggleButton,
  FilterToggleButtonDisabled,
} from './Filter.style';
import { FilterSlider } from './FilterSlider';

type Props = {
  title: string;
  options: Option[];
  model: string;
  type: string;
  label: string;
  defaultValue: any;
  onApplyFilter: (selectedFilters: {}) => void;
  clearFilter: (model: string) => void;
  onFilterSelectionChange: (
    data: ProviderSearchFilters,
    lastModifiedModel: string
  ) => void;
  onPopoverDismiss: () => void;
  totalCount: number;
  isDisabled: boolean;
  isLoading: boolean;
  enableMapViewEnhancements?: boolean;
  firstFiltersRendered: ProviderFiltersType[];
  sectionType?: string;
};

const isAreaOfExpertise = (model) =>
  model === FilterCollectionModel.AREA_OF_EXPERTISE;

const isEAPOffered = (model) => model === FilterCollectionModel.EAP_OFFERED;

const getDefaultValuesExcludingEAP = (defaultvalues) =>
  isArray(defaultvalues)
    ? defaultvalues.filter((item) =>
        item.dnt_label !== EAP_OPTION_LABEL ? item.value : undefined
      )
    : defaultvalues;

const getTooltipContent = (
  isDistance: boolean,
  isEAP: boolean,
  isDisabledByPriorityScheduling: boolean,
  t: any
): string => {
  if (isDisabledByPriorityScheduling) {
    return t('CONTENT_FILTERS.PRIORITY_SCHEDULING_DISABLED_TOOLTIP');
  }
  if (isDistance) {
    return t('CONTENT_FILTERS.DISTANCE_DISABLED_TOOLTIP');
  }
  if (isEAP) {
    return t('CONTENT_FILTERS.EAP_DISABLED_TOOLTIP');
  }
  return t('CONTENT_FILTERS.GENERIC_FILTER_DISABLED_TOOLTIP');
};

export const NameFilter = ({
  title = '',
  options = [],
  model = '',
  type = '',
  label = '',
  defaultValue: defaultValueFromProps,
  onApplyFilter,
  clearFilter,
  onFilterSelectionChange,
  onPopoverDismiss,
  totalCount = 0,
  isDisabled = false,
  isLoading,
  enableMapViewEnhancements = false,
  sectionType,
  firstFiltersRendered,
}: Props) => {
  const [showFilter, setShowFilter] = useState(false);
  const isInteractingWithPopoverRef = useRef(false);

  const [
    enhanceDistanceFilter,
    enableShowDefaultSearchRadiusPopoverMsg,
    enableVirtualCareFilterAnalytics,
    enableShowUpcomingAvailabilityPopoverMsgForBH,
    enableShowPrioritySchedulingPopoverMsg,
    enableBhGroupedAoeFilter,
    isSedationPerformed,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENHANCE_DISTANCE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_DEFAULT_SEARCH_RADIUS_POPOVER_MSG,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_ANALYTICS_V2,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_UPCOMING_AVAILABILITY_POPOVER_MSG_FOR_BH,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_PRIORITY_SCHEDULING_POPOVER_MSG,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.SEDATION_DENTISTRY_SERVICES_PERFORMED,
  ]);

  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const defaultSearchRadiusMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.DEFAULT_SEARCH_RADIUS_MESSAGE,
  });
  const upcomingAvailabilityMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.UPCOMING_AVAILABILITY_MESSAGE,
  });

  const prioritySchedulingMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.PRIORITY_SCHEDULING_COACHMARK_MESSAGE,
  });

  const distanceFilterMaxValidityDaysMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.DISTANCE_FILTER_MAX_VALIDITY_DAYS,
  });
  const distanceMaxValidityDays = parseInt(
    distanceFilterMaxValidityDaysMessage?.message || '0',
    10
  );

  const upcomingAvailabilityMaxValidityDaysMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING
      .UPCOMING_AVAILABILITY_FILTER_MAX_VALIDITY_DAYS,
  });
  const upcomingAvailabilityMaxValidityDays = parseInt(
    upcomingAvailabilityMaxValidityDaysMessage?.message || '0',
    10
  );

  const prioritySchedulingMaxValidityDaysMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING
      .PRIORITY_SCHEDULING_FILTER_MAX_VALIDITY_DAYS,
  });
  const prioritySchedulingMaxValidityDays = parseInt(
    prioritySchedulingMaxValidityDaysMessage?.message || '0',
    10
  );

  const hsidUuid = useHsidUuid();

  const { portalData } = useContext(PortalContext);
  const { searchFilters } = useContext(SearchFilterContext);
  const portalConfigCheck = portalData?.portalConfig?.theme === 'optum';

  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );

  const [educationalContent, getEducationalContentStatus] =
    useEducationalContentStatus({});
  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({});
  const [showDistancePopover, setShowDistancePopover] = useState(false);
  const [showUpcomingAvailabilityPopover, setShowUpcomingAvailabilityPopover] =
    useState(false);
  const [showPrioritySchedulingPopover, setShowPrioritySchedulingPopover] =
    useState(false);

  const distanceFilterDontShowButtonClickedDate = useStore(
    StoreKeys.DISTANCE_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const setDistanceFilterDontShowButtonClickedDate = useStore(
    StoreKeys.SET_DISTANCE_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const currentDate = new Date().toISOString().split('T')[0];

  const upcomingAvailabilityFilterDontShowButtonClickedDate = useStore(
    StoreKeys.UPCOMING_AVAILABILITY_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const setUpcomingAvailabilityFilterDontShowButtonClickedDate = useStore(
    StoreKeys.SET_UPCOMING_AVAILABILITY_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const prioritySchedulingFilterDontShowButtonClickedDate = useStore(
    StoreKeys.PRIORITY_SCHEDULING_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const setPrioritySchedulingFilterDontShowButtonClickedDate = useStore(
    StoreKeys.SET_PRIORITY_SCHEDULING_FILTER_DONT_SHOW_BUTTON_CLICKED_DATE
  );

  const { searchRadius } = useSearchStore(
    useShallow((state: SearchState) => ({
      searchRadius: state.searchStore.searchRadius,
    }))
  );
  const defaultValue = isArray(defaultValueFromProps)
    ? defaultValueFromProps.filter((item) => item.value)
    : defaultValueFromProps;

  const { t } = useTranslation();
  const isShowSelected = isArray(defaultValue)
    ? defaultValue?.length && !defaultValue?.[0].hide
    : defaultValue?.value;

  const chipsFilters: string = Constants.PROVIDER_RESULTS.FILTER_BY;

  const typeaheadState = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      searchMethod: state.typeaheadSearchStore.searchMethod,
      searchTerm: state.typeaheadSearchStore.searchTerm,
      virtualCare: state.typeaheadSearchStore.virtualCare,
      gender: state.typeaheadSearchStore.gender,
      specialtyCategory: state.typeaheadSearchStore.specialtyCategory,
      condition: state.typeaheadSearchStore.condition,
    }))
  );
  let updatedDefaultValue = defaultValue;
  if (isAreaOfExpertise(model)) {
    updatedDefaultValue = getDefaultValuesExcludingEAP(updatedDefaultValue);
  }

  const isBccActive = isPrioritySchedulingActive(searchFilters);

  const handleApply = (data) => {
    isInteractingWithPopoverRef.current = false;
    setShowFilter(false);
    let selectedFilter;

    if (type === FilterOptionsType.RADIO) {
      selectedFilter = {
        [model]: options.filter((option) => data === option.value.toString()),
      };
    } else if (type === FilterOptionsType.SLIDER) {
      selectedFilter = {
        [model]: options.filter((option) => data === option.c_value),
      };
    } else {
      selectedFilter = {
        [model]: options.filter((option) =>
          data.includes(option.value.toString())
        ),
      };
    }
    onApplyFilter(selectedFilter);
    adobeSearchFilterEvent({
      filters: getFormattedFilters(selectedFilter),
      term: getSearchTerm(typeaheadState),
    });
  };
  const findDifferenceInDays = (date1: string, date2: string): number => {
    const parsedDate1 = new Date(date1);
    const parsedDate2 = new Date(date2);

    const msInDay = 24 * 60 * 60 * 1000; // Milliseconds in a day
    return Math.floor(
      (parsedDate1.getTime() - parsedDate2.getTime()) / msInDay
    );
  };

  const onOptionChange = (e: { [key: string]: Option[] }) => {
    isInteractingWithPopoverRef.current = true;
    const lastModifiedModel =
      Object.values(e)[0].length > 0 ? Object.keys(e)[0] : '';
    onFilterSelectionChange(e, lastModifiedModel);
  };

  const handleOnClearFilter = (event) => {
    clearFilter(model);
    event.stopPropagation();
    adobeLinkTrackEvent({
      name: `${label}:clear`,
      location: `body:${chipsFilters}`,
    });
  };

  const onOpenAnalyticsHandler = () => {
    const linkName: string = label;
    const linkLocation: string = `body:${chipsFilters}`;
    const modalName: string = `${label} filter`;

    const expectedVirtualCareLabel = t('CONTENT_FILTERS.VIRTUAL_CARE_OPTIONS');

    if (
      linkName !== expectedVirtualCareLabel ||
      enableVirtualCareFilterAnalytics
    ) {
      handleLinkAndModalTrack(linkName, linkLocation, modalName);
    }
  };

  const isEAPOfferedModel = isEAPOffered(model);
  const getDefaultValue = (defaultValue: any, options: any) => {
    if (isArray(defaultValue)) {
      // for eap value, they are not consistent
      if (isEAPOfferedModel) {
        return defaultValue
          .map((item: Option) => {
            const option = options.find(
              (opt) => item.dnt_label === opt.dnt_label
            );
            if (option) {
              return { ...item, value: option.value };
            }
            return item;
          })
          .filter((item) =>
            options.some((opt) =>
              item.dnt_label === opt.dnt_label
                ? (item.value = opt.value)
                : false
            )
          )
          .map((item) => item.value.toString());
      }

      return defaultValue
        .map((item: Option) => {
          const option = options.find((opt) => opt.value === item.value);
          if (option) {
            return { ...item, value: option.value };
          }
          return item;
        })
        .filter(
          (item) =>
            options.some((opt) => opt.value === item.value) ||
            (model === FilterCollectionModel.UPCOMING_AVAILABILITY &&
              isBccActive &&
              String(item.value) === '7')
        )
        .map((item) => item.value.toString());
    }

    return [defaultValue?.value];
  };

  const selectedOptions = getDefaultValue(updatedDefaultValue, options).length;
  const [distanceTitle, setDistanceTitle] = useState('');
  const [isFirstClickDistance, setIsFirstClickDistance] = useState(false);
  const [
    isFirstClickUpcomingAvailability,
    setIsFirstClickUpcomingAvailability,
  ] = useState(false);
  const [isFirstClickPriorityScheduling, setIsFirstClickPriorityScheduling] =
    useState(false);
  const filterSortScreen = useMediaQuery(viewFilterSortScreen);
  const [showCoachMarkWebDistance, setShowCoachMarkWebDistance] =
    useState(true);
  const [
    showCoachMarkUpcomingAvailability,
    setShowCoachMarkUpcomingAvailability,
  ] = useState(true);
  const [showCoachMarkPriorityScheduling, setShowCoachMarkPriorityScheduling] =
    useState(true);

  const filterMessageClosedForDistanceSessionValue =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION.FILTER_MESSAGE_CLOSED_FOR_SESSION_DISTANCE
    ) ?? '';

  const filterMessageClosedForUpcomingAvailabilitySessionValue =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION
        .FILTER_MESSAGE_CLOSED_FOR_SESSION_UPCOMING_AVAILABILITY
    ) ?? '';

  const filterMessageClosedForPrioritySchedulingSessionValue =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION
        .FILTER_MESSAGE_CLOSED_FOR_SESSION_PRIORITY_SCHEDULING
    ) ?? '';

  const psCookie = storage.cookie.get(Constants.COOKIES.PLAN_SELECTOR);
  const sessionIdOBH = storage.cookie.get('session_id');
  const sessionIdUMR = storage.cookie
    .get('FINDCARE_AND_COST_SSO_SESSION')
    ?.split('!')
    ?.at(1);
  const guestSessionCookie = storage.cookie.get('PSX_GUEST_SESSION');
  const sessionKey =
    psCookie || sessionIdOBH || sessionIdUMR || guestSessionCookie || 'Close';
  const isDistanceCoachMarkClosed =
    sessionKey?.length > 0 &&
    filterMessageClosedForDistanceSessionValue === sessionKey;
  const isUpcomingAvailabilityCoachMarkClosed =
    sessionKey?.length > 0 &&
    filterMessageClosedForUpcomingAvailabilitySessionValue === sessionKey;
  const isPrioritySchedulingCoachMarkClosed =
    sessionKey?.length > 0 &&
    filterMessageClosedForPrioritySchedulingSessionValue === sessionKey;
  const isGuest = storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST);
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;

  // Check for enableBccFilter URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const enableBccSearchFromUrl = urlParams.get('enableBccSearch') === 'true';

  const isBH = coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];
  const isBhCoverage =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
  const isPractitioner =
    sectionType?.toLowerCase() === SectionType.PROVIDER.toLowerCase();

  useEffect(() => {
    const defaultValueResult = getDefaultValue(defaultValue, options);
    const computedDistanceTitle = `${label}: ${defaultValueResult[0]} ${
      defaultValueResult[0] > 1
        ? t('CONTENT_FILTERS.MILES')
        : t('CONTENT_FILTERS.MILE')
    }`;
    setDistanceTitle(computedDistanceTitle);
  }, [defaultValueFromProps]);

  useEffect(() => {
    getEducationalContentStatus({
      variables: { isSuperUser, ...(hsidUuid && { userId: hsidUuid }) },
    });
  }, []);

  useEffect(() => {
    if (isFirstClickDistance) {
      setShowFilter(false);
    }
  }, [isFirstClickDistance]);

  useEffect(() => {
    if (isFirstClickUpcomingAvailability) {
      setShowFilter(false);
    }
  }, [isFirstClickUpcomingAvailability]);

  useEffect(() => {
    if (isFirstClickPriorityScheduling) {
      setShowFilter(false);
    }
  }, [isFirstClickPriorityScheduling]);

  const renderFilterOptions = () => {
    const submitBtnName = !isLoading
      ? t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.SHOW_RESULT_BUTTON').replace(
          '$count',
          totalCount?.toString()
        )
      : t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.LOADING');
    options =
      model == FilterCollectionModel.SERVICES_OFFERED
        ? isSedationPerformed
          ? options
          : options.filter(
              (option) =>
                option.label !== FilterCollectionModel.SEDATION_DENTISTRY
            )
        : options;
    const disableSubmitBtn = isLoading || totalCount === 0;
    switch (type) {
      case FilterOptionsType.RADIO:
        return (
          <RadioGroup
            data-auto-testid={`${model}-chip-filter`}
            data-testid={`${model}-chip-filter`}
            defaultValue={defaultValue?.length ? defaultValue[0].value : ''}
            disableSubmitBtn={disableSubmitBtn}
            id={label}
            locationForAnalytics={`modal:${label} filter`}
            model={model}
            onApply={(data) => {
              handleApply(data);
            }}
            onOptionChange={onOptionChange}
            options={options}
            submitBtnName={submitBtnName}
          />
        );
      case FilterOptionsType.CHECK_BOX:
        if (
          model === FilterCollectionModel.AREA_OF_EXPERTISE &&
          enableBhGroupedAoeFilter &&
          isBhCoverage
        ) {
          return (
            <GroupedAoeFilter
              defaultValue={
                Array.isArray(defaultValue)
                  ? defaultValue.map((item: Option) => String(item.value))
                  : []
              }
              disableSubmitBtn={disableSubmitBtn}
              isDisabled={isDisabled}
              locationForAnalytics={`modal:${label} filter`}
              model={model}
              onApply={(data) => {
                handleApply(data);
              }}
              onOptionChange={onOptionChange}
              options={options}
              showApplyButton
              submitBtnName={submitBtnName}
            />
          );
        }

        return (
          <CheckBoxGroup
            data-auto-testid={`${model}-chip-filter`}
            data-testid={`${model}-chip-filter`}
            defaultValue={getDefaultValue(defaultValue, options)}
            disableSubmitBtn={disableSubmitBtn}
            locationForAnalytics={`modal:${label} filter`}
            model={model}
            onApply={(data) => {
              handleApply(data);
            }}
            onOptionChange={onOptionChange}
            options={options}
            submitBtnName={submitBtnName}
          />
        );
      case FilterOptionsType.SLIDER:
        return (
          <FilterSlider
            data-auto-testid={`${model}-slider-filter`}
            data-testid={`${model}-slider-filter`}
            defaultValue={defaultValue?.length ? defaultValue[0].value : 0}
            disableSubmitBtn={disableSubmitBtn}
            locationForAnalytics={`modal:${label} filter`}
            model={model}
            onApply={(data) => {
              handleApply(data);
            }}
            onOptionChange={onOptionChange}
            options={options}
            submitBtnName={submitBtnName}
          />
        );
      default:
        return null;
    }
  };

  const isFirstUpcomingAvalabilityRendered = firstFiltersRendered?.some(
    (filter) => filter.model === FilterCollectionModel.UPCOMING_AVAILABILITY
  );

  useEffect(() => {
    if (firstFiltersRendered.length > 0) {
      const clickedDate =
        distanceFilterDontShowButtonClickedDate ||
        educationalContent?.data?.educationalContentStatus
          ?.distanceFilterBtnClickedDate ||
        '';

      if (!distanceFilterDontShowButtonClickedDate) {
        setDistanceFilterDontShowButtonClickedDate(clickedDate);
      }
      if (!isFirstUpcomingAvalabilityRendered || !(isBH && isPractitioner)) {
        if (clickedDate === '') {
          setShowDistancePopover(true);
        } else {
          const difference = findDifferenceInDays(currentDate, clickedDate);
          setShowDistancePopover(difference > distanceMaxValidityDays);
        }
      }
    }
  }, [
    educationalContent?.data,
    distanceFilterDontShowButtonClickedDate,
    firstFiltersRendered,
  ]);

  useEffect(() => {
    const clickedDate =
      upcomingAvailabilityFilterDontShowButtonClickedDate ||
      educationalContent?.data?.educationalContentStatus
        ?.upcomingAvailabilityFilterBtnClickedDate ||
      '';

    if (!upcomingAvailabilityFilterDontShowButtonClickedDate) {
      setUpcomingAvailabilityFilterDontShowButtonClickedDate(clickedDate);
    }
    if (clickedDate === '') {
      setShowUpcomingAvailabilityPopover(true);
    } else {
      const difference = findDifferenceInDays(currentDate, clickedDate);
      setShowUpcomingAvailabilityPopover(
        difference > upcomingAvailabilityMaxValidityDays
      );
    }
  }, [
    educationalContent?.data,
    upcomingAvailabilityFilterDontShowButtonClickedDate,
  ]);

  useEffect(() => {
    const clickedDate =
      prioritySchedulingFilterDontShowButtonClickedDate ||
      educationalContent?.data?.educationalContentStatus
        ?.prioritySchedulingFilterBtnClickedDate ||
      '';

    if (!prioritySchedulingFilterDontShowButtonClickedDate) {
      setPrioritySchedulingFilterDontShowButtonClickedDate(clickedDate);
    }
    if (clickedDate === '') {
      setShowPrioritySchedulingPopover(true);
    } else {
      const difference = findDifferenceInDays(currentDate, clickedDate);
      setShowPrioritySchedulingPopover(
        difference > prioritySchedulingMaxValidityDays
      );
    }
  }, [
    educationalContent?.data,
    prioritySchedulingFilterDontShowButtonClickedDate,
  ]);

  const buttonTestId = `${label
    .replace(/ /g, '-')
    .toLocaleLowerCase()}-toggle-filter-button`;
  const isDistance = model === FilterCollectionModel.DISTANCE;
  const isUpcomingAvailability =
    model === FilterCollectionModel.UPCOMING_AVAILABILITY;
  const isProviderScheduling =
    model === FilterCollectionModel.PRIORITY_SCHEDULING;
  const hasSelectedOptions = selectedOptions > 0;
  const showSelectedOptions = isShowSelected && hasSelectedOptions;

  const isEAP = isEAPOfferedModel && showSelectedOptions && isBH;

  // Check if this filter is disabled due to Priority Scheduling
  const isDisabledByPriorityScheduling = isFilterDisabledByBcc(
    model,
    isBccActive
  );
  const showSelectedOptionsWhenDisabled =
    hasSelectedOptions && (isShowSelected || isDisabledByPriorityScheduling);

  if (isDisabled || isDisabledByPriorityScheduling) {
    const tooltipContent = getTooltipContent(
      isDistance,
      isEAP,
      isDisabledByPriorityScheduling,
      t
    );

    return (
      <Tooltip content={tooltipContent}>
        <FilterToggleButtonDisabled
          css={{
            '&.abyss-button-root': {
              tooltip: '188px !important',
            },
          }}
          data-testid={`${buttonTestId}-disabled`}
          isDisabled={true}
        >
          {label}
          {showSelectedOptionsWhenDisabled
            ? isEAPOfferedModel || isDisabledByPriorityScheduling
              ? ` (${selectedOptions})`
              : `: ${t('N/A')}`
            : ''}
          <IconMaterial color="$descriptionBlack" icon="expand_more" />
        </FilterToggleButtonDisabled>
      </Tooltip>
    );
  }

  const handleCrossIconClick = () => {
    if (isDistance && !isFirstClickDistance) {
      setShowCoachMarkWebDistance(false);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION
          .FILTER_MESSAGE_CLOSED_FOR_SESSION_DISTANCE,
        sessionKey
      );
      setIsFirstClickDistance(true);
    }
    if (isUpcomingAvailability && !isFirstClickUpcomingAvailability) {
      setShowCoachMarkUpcomingAvailability(false);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION
          .FILTER_MESSAGE_CLOSED_FOR_SESSION_UPCOMING_AVAILABILITY,
        sessionKey
      );
      setIsFirstClickUpcomingAvailability(true);
    }
    if (isProviderScheduling && !isFirstClickPriorityScheduling) {
      setShowCoachMarkPriorityScheduling(false);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION
          .FILTER_MESSAGE_CLOSED_FOR_SESSION_PRIORITY_SCHEDULING,
        sessionKey
      );
      setIsFirstClickPriorityScheduling(true);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      const activeElementClasses = document.activeElement?.classList;
      const crossIconClicked = activeElementClasses?.contains(
        'abyss-popover-close-button'
      );
      if (crossIconClicked) {
        handleCrossIconClick();
      }
    }
  };
  const handleDontShowAgain = () => {
    if (isDistance) {
      setEducationalContentStatus({
        variables: {
          distanceFilterBtnClickedDate: currentDate,
          hasViewedContent:
            educationalContent?.data?.educationalContentStatus
              ?.hasViewedContent,
          ...(hsidUuid && { userId: hsidUuid }),
          isSuperUser,
        },
      });
      setDistanceFilterDontShowButtonClickedDate(
        educationalContent?.data?.educationalContentStatus
          ?.distanceFilterBtnClickedDate || ''
      );
      setShowCoachMarkWebDistance(false);
      setIsFirstClickDistance(true);
    }
    if (isUpcomingAvailability) {
      setEducationalContentStatus({
        variables: {
          upcomingAvailabilityFilterBtnClickedDate: currentDate,
          hasViewedContent:
            educationalContent?.data?.educationalContentStatus
              ?.hasViewedContent,
          ...(hsidUuid && { userId: hsidUuid }),
          isSuperUser,
        },
      });

      setUpcomingAvailabilityFilterDontShowButtonClickedDate(
        educationalContent?.data?.educationalContentStatus
          ?.upcomingAvailabilityFilterBtnClickedDate || ''
      );
      setShowCoachMarkUpcomingAvailability(false);
      setIsFirstClickUpcomingAvailability(true);
    }
    if (isProviderScheduling) {
      setEducationalContentStatus({
        variables: {
          prioritySchedulingFilterBtnClickedDate: currentDate,
          hasViewedContent:
            educationalContent?.data?.educationalContentStatus
              ?.hasViewedContent,
          ...(hsidUuid && { userId: hsidUuid }),
          isSuperUser,
        },
      });

      setPrioritySchedulingFilterDontShowButtonClickedDate(
        educationalContent?.data?.educationalContentStatus
          ?.prioritySchedulingFilterBtnClickedDate || ''
      );
      setShowCoachMarkPriorityScheduling(false);
      setIsFirstClickPriorityScheduling(true);
    }
  };

  const popOverContent = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <p style={{ textAlign: 'left', width: '100%' }}>
        {defaultSearchRadiusMessage?.message?.replace(
          '{{miles}}',
          searchRadius
        )}
      </p>
      {!isGuest && (
        <Button data-testid="popover-button" onClick={handleDontShowAgain}>
          {t('DontShowAgain')}
        </Button>
      )}
    </div>
  );

  const popOverContentUpcomingAvailability = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <p style={{ textAlign: 'left', width: '100%' }}>
        {upcomingAvailabilityMessage?.message}
      </p>
      {!isGuest && (
        <Button data-testid="popover-button" onClick={handleDontShowAgain}>
          {t('DontShowAgain')}
        </Button>
      )}
    </div>
  );

  const popOverContentPriorityScheduling = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <p style={{ textAlign: 'left', width: '100%' }}>
        {prioritySchedulingMessage?.message}
      </p>
      {!isGuest && (
        <Button data-testid="popover-button" onClick={handleDontShowAgain}>
          {t('DontShowAgain')}
        </Button>
      )}
    </div>
  );

  const showPrioritySchedulingCoachmark =
    enableBccSearchFromUrl &&
    enableShowPrioritySchedulingPopoverMsg &&
    !isPrioritySchedulingCoachMarkClosed &&
    showPrioritySchedulingPopover;

  const showUpcomingAvailabilityCoachmark =
    !enableBccSearchFromUrl &&
    isBH &&
    isPractitioner &&
    enableShowUpcomingAvailabilityPopoverMsgForBH &&
    !isUpcomingAvailabilityCoachMarkClosed &&
    showUpcomingAvailabilityPopover;

  const showDistanceCoachMark =
    !enableBccSearchFromUrl &&
    !!selectedOptions &&
    !isDistanceCoachMarkClosed &&
    showDistancePopover &&
    enableShowDefaultSearchRadiusPopoverMsg;

  const showUpcomingAvailabilityFilter =
    isUpcomingAvailability && showUpcomingAvailabilityCoachmark;

  const showPrioritySchedulingFilter =
    isProviderScheduling && showPrioritySchedulingCoachmark;

  const filterLabelContent = renderFilterLabel(
    showUpcomingAvailabilityFilter,
    showCoachMarkUpcomingAvailability,
    handleOpenChange,
    filterSortScreen,
    portalConfigCheck,
    upcomingAvailabilityMessage?.title,
    label,
    isDistance,
    distanceTitle,
    showSelectedOptions,
    selectedOptions,
    showDistanceCoachMark,
    showCoachMarkWebDistance,
    defaultSearchRadiusMessage?.title,
    popOverContent(),
    popOverContentUpcomingAvailability(),
    showPrioritySchedulingFilter,
    showCoachMarkPriorityScheduling,
    prioritySchedulingMessage?.title,
    popOverContentPriorityScheduling()
  );

  const isGEHAV2 = shouldUseGehaV2Styling();
  const isUpdatedSelected =
    isShowSelected &&
    (updatedDefaultValue.length > 0 ||
      (isProviderScheduling && defaultValue?.value));
  const isEnhancedDistanceFilter = enhanceDistanceFilter && isDistance;

  let filterToggleClass = 'unset';
  if (isUpdatedSelected) {
    filterToggleClass = 'selected';
  } else if (enableMapViewEnhancements) {
    filterToggleClass = 'map-view';
  }

  return (
    <FilterPopOverWrapper>
      <Popover
        content={renderFilterOptions()}
        css={{
          'abyss-popover-content': {
            display: 'flex',
            border: 'none',
            borderRadius: '12px',
            padding: '24px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '20px',
            alignSelf: 'stretch',
            maxHeight:
              'var(--radix-popper-available-height, calc(100vh - 200px))',
            overflowY: 'auto',
            overflowX: 'hidden',
            '-ms-overflow-style': 'none',
            scrollPaddingBottom: '200px',
            scrollPaddingTop: '200px',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
          'abyss-popover-header': {
            height: '32px',
            marginLeft: '4px',
            marginBottom: '20px',
            padding: '7px 0px',
            width: '327px',
          },
          'abyss-popover-close-button': {
            top: '35px',
            right: '24px',
            position: 'absolute',
          },
          'abyss-popover-title': {
            marginTop: '0',
            fontSize: '$largeText !important',
            fontWeight: '$bold !important',
            lineHeight: '28px !important',
          },
          'abyss-popover-arrow': {
            display: 'none !important',
          },
        }}
        data-testid={`${label.replace(/ /g, '-').toLocaleLowerCase()}`}
        onOpenChange={(open) => {
          if (!open && isInteractingWithPopoverRef.current) {
            isInteractingWithPopoverRef.current = false;
            return;
          }

          // Priority 1: Priority Scheduling (when enableBccSearchFromUrl is true)
          if (
            isProviderScheduling &&
            enableBccSearchFromUrl &&
            enableShowPrioritySchedulingPopoverMsg
          ) {
            setShowCoachMarkPriorityScheduling(false);
            storage.session.set(
              Constants.STORAGE_KEYS.SESSION
                .FILTER_MESSAGE_CLOSED_FOR_SESSION_PRIORITY_SCHEDULING,
              sessionKey
            );
          }

          // Priority 2: Upcoming Availability (only if Priority Scheduling is not showing)
          if (
            isUpcomingAvailability &&
            !showPrioritySchedulingCoachmark &&
            isBH &&
            isPractitioner &&
            enableShowUpcomingAvailabilityPopoverMsgForBH &&
            !enableBccSearchFromUrl
          ) {
            setShowCoachMarkUpcomingAvailability(false);
            storage.session.set(
              Constants.STORAGE_KEYS.SESSION
                .FILTER_MESSAGE_CLOSED_FOR_SESSION_UPCOMING_AVAILABILITY,
              sessionKey
            );
          }

          // Priority 3: Distance (only if neither Priority Scheduling nor Upcoming Availability is showing)
          if (
            isDistance &&
            !showPrioritySchedulingCoachmark &&
            !showUpcomingAvailabilityCoachmark &&
            enableShowDefaultSearchRadiusPopoverMsg
          ) {
            setShowCoachMarkWebDistance(false);
            showDistanceCoachMark &&
              storage.session.set(
                Constants.STORAGE_KEYS.SESSION
                  .FILTER_MESSAGE_CLOSED_FOR_SESSION_DISTANCE,
                sessionKey
              );
          }

          setShowFilter(open);
          if (!open) {
            onPopoverDismiss();
          } else {
            onOpenAnalyticsHandler();
          }
        }}
        open={showFilter}
        position="bottom"
        title={title}
        width={375}
      >
        <FilterToggleButton
          className={filterToggleClass}
          css={{
            '&.selected': {
              background: isGEHAV2 ? '#CAD9E2' : '',
            },
          }}
          data-auto-testid={buttonTestId}
          data-testid={buttonTestId}
        >
          {filterLabelContent}
          <IconMaterial
            color="$descriptionBlack"
            icon={showFilter ? 'expand_less' : 'expand_more'}
          />

          {isUpdatedSelected && !isEnhancedDistanceFilter ? (
            <div
              aria-label={`${t(
                'CONTENT_FILTERS.DISMISS'
              )} ${label.toLowerCase()} ${t(
                'CONTENT_FILTERS.FILTER_SELECTION'
              )}`}
              data-auto-testid={`${model}-clear-filter`}
              data-testid={`${model}-clear-filter`}
              onClick={(e) => handleOnClearFilter(e)}
            >
              <IconMaterial color="$descriptionBlack" icon="close" />
            </div>
          ) : null}
        </FilterToggleButton>
      </Popover>
    </FilterPopOverWrapper>
  );
};
